import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { FormInstanceResponse } from "../models/FormInstance";

const moment = getMomentTz();

type useGetFormsProps = {
	start: Date;
	end: Date;
	formInstanceStatusCodes: string | null;
};

export const getForms = async (
	{ start, end, formInstanceStatusCodes }: useGetFormsProps,
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "Start", value: start },
		{ name: "End", value: end },
		{
			name: "FormInstanceStatusCodes",
			value: formInstanceStatusCodes === "All" ? null : formInstanceStatusCodes,
		},
	];

	const resp = await apiService.callV2(
		"[FORMS].[GetFormInstancesByRange]",
		parameters,
		signal,
	);

	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []) as FormInstanceResponse[];

		rows = rows.map((f) => ({
			...f,
			OpenTimestamp: moment.utc(f["OpenTimestamp"]).toDate(),
			SubmitTimestamp: f.SubmitTimestamp
				? moment.utc(f["SubmitTimestamp"]).toDate()
				: null,
		}));

		return rows;
	} else {
		throw new Error(resp.message || "Error fetching form instances");
	}
};

export const getFormInstance = async (
	params: { formInstanceId: number | null | undefined; checkAnswers: boolean },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{
			name: "FormInstanceId",
			value: params.formInstanceId ? params.formInstanceId : null,
		},
		{
			name: "CheckAnswers",
			value: params.checkAnswers,
		},
	];

	const resp = await apiService.callV2(
		"[FORMS].[GetFormInstanceFlowWithAnswersV1]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as FormInstanceResponse[];
	} else {
		throw new Error(resp.message || "Error fetching form instances");
	}
};

export const upsertAnswerFromJSON = async ({
	formInstanceId,
	comments,
	jsonData,
}: {
	formInstanceId: number;
	comments: string | null;
	jsonData: string | null;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "FormInstanceId", value: formInstanceId },
		{ name: "Comments", value: comments },
		{ name: "jsonData", value: jsonData },
	];

	const resp = await apiService.callV2(
		`[FORMS].[UpsertAnswersFromJSON]`,
		parameters,
	);
	return resp;
};

export const upsertAnswer = async ({
	formInstanceId,
	questionId,
	answer,
}: {
	formInstanceId: number;
	questionId: number | null;
	answer: string | number | undefined | null;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "FormInstanceId", value: formInstanceId },
		{ name: "QuestionId", value: questionId },
		{ name: "Answer", value: answer ?? null },
	];

	const resp = await apiService.callV2(`[FORMS].[UpsertAnswer]`, parameters);
	return resp;
};

export const createFormInstance = async (formId: number | null) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "FormId", value: formId }];

	const resp = await apiService.callV2(
		"[FORMS].[CreateFormInstance]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error creating form instance");
	}
};

export const submitFormInstance = async ({
	formInstanceId,
	comments,
}: {
	formInstanceId: number | null;
	comments: string | null;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "FormInstanceId", value: formInstanceId },
		{ name: "Comments", value: comments },
		{ name: "SubmitSignature", value: "" },
	];

	return await apiService.callV2("[FORMS].[SubmitFormInstance]", parameters);
};

export const removeInstance = async (instanceId: number | null) => {
	if (instanceId === null) return;
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];
	parameters.push({ name: "FormInstanceId", value: instanceId });

	return await apiService.callV2("[FORMS].[DeleteFormInstance]", parameters);
};
